import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Hero from './components/Hero';
import Vision from './components/Vision';
import Services from './components/Services';
import CSR from './components/CSR';
import CompanyProfile from './components/CompanyProfile';
import Contact from './components/Contact';
import Footer from './components/Footer';
import LanguageSwitcher from '../../components/feature/LanguageSwitcher';

export default function HomePage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /* -------------------------------------------------------------------------
   * SEO / Schema effect
   * ----------------------------------------------------------------------- */
  useEffect(() => {
    // ----- page title -------------------------------------------------------
    document.title =
      'マンダラチャート認定講師｜福岡市LIKES（ライクス）組織開発・キャッシュレス決済 株式会社LIKES';

    // ----- meta description -------------------------------------------------
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        '福岡市東区の株式会社LIKES（ライクス）。マンダラチャート認定講師による組織風土改革・人材育成コンサルティングとB2Bキャッシュレス決済導入支援を提供。マンダラを活用した目標設定で企業の経営課題を根本から解決します。'
      );
    }

    // ----- canonical link ---------------------------------------------------
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute(
        'href',
        `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      );
    }

    // ----- schema.org JSON‑LD -----------------------------------------------
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: '株式会社LIKES',
      alternateName: ['LIKES Co, Ltd', 'ライクス', '福岡市LIKES'],
      url: siteUrl,
      description:
        '福岡市東区を拠点に、組織風土改革・人材育成コンサルティングとB2Bキャッシュレス決済導入支援を提供する経営コンサルティング会社。',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '香椎照葉3-2-7',
        addressLocality: '福岡市東区',
        addressRegion: '福岡県',
        postalCode: '813-0017',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '33.6190',
        longitude: '130.4184',
      },
      foundingDate: '2015-06',
      founder: {
        '@type': 'Person',
        name: '阿高 佳男',
        alternateName: 'Yoshio ATAKA',
        jobTitle: '代表取締役',
      },
      areaServed: [
        { '@type': 'City', name: '福岡市' },
        { '@type': 'AdministrativeArea', name: '福岡県' },
      ],
      hasCredential: 'マンダラチャート認定講師',
      serviceType: ['組織開発コンサルティング', '人材育成', 'キャッシュレス決済導入支援', 'DX支援'],
    };

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      name: '株式会社LIKES',
      url: siteUrl,
      description: '福岡市の組織開発コンサルティングとキャッシュレス決済導入支援',
      publisher: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
      },
      inLanguage: 'ja',
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: `${siteUrl}/`,
      name: '福岡市LIKES（ライクス）| 組織開発・キャッシュレス決済導入支援 株式会社LIKES',
      description: '福岡市東区の株式会社LIKES。組織風土改革・人材育成コンサルティングとB2Bキャッシュレス決済導入支援。',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      inLanguage: 'ja',
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: `${siteUrl}/`,
        },
      ],
    };

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '株式会社LIKESはどのようなサービスを提供していますか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '組織風土の改革・人材育成コンサルティングと、B2Bに特化したキャッシュレス決済導入支援の2つを主軸としています。マンダラチャート認定講師による人材育成から、GMO・ライフカードと連携した決済システム導入まで幅広く対応します。',
          },
        },
        {
          '@type': 'Question',
          name: 'マンダラチャートとはどのようなツールですか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'マンダラチャートは3×3の9マスを基本構造とした目標設定ツールです。大谷翔平選手が高校時代に活用したことで広く知られています。中心に目標を置き、その達成に必要な8つの要素を周囲のマスに展開。さらに各要素を具体的な行動レベルまで落とし込む9×9マスの思考フレームワークです。株式会社LIKESはマンダラチャート認定講師が企業の人材育成に活用しています。',
          },
        },
        {
          '@type': 'Question',
          name: 'マンダラチャートを使った人材育成の特徴は何ですか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'マンダラチャートを活用した人材育成では、経営ビジョンから日々の業務まで一貫した目標設定が可能です。新入社員から管理職まで階層を問わず活用でき、個人の目標とチームの目標を連携させることで組織全体のパフォーマンスを向上させます。株式会社LIKESのマンダラチャート認定講師が実践的なワークショップを通じて習得を支援します。',
          },
        },
        {
          '@type': 'Question',
          name: '株式会社LIKESの所在地はどこですか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '福岡県福岡市東区香椎照葉3-2-7に所在しています。福岡市を中心に、九州全域の企業様を支援しています。',
          },
        },
        {
          '@type': 'Question',
          name: '代表取締役は誰ですか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '代表取締役は阿高 佳男（Yoshio ATAKA）です。500名規模の組織における人事評価制度の構築や、事業部門の経営改善コンサルティングなど豊富な実績を持つマンダラチャート認定講師です。',
          },
        },
      ],
    };

    const schemas = [
      { id: 'schema-organization', data: organizationSchema },
      { id: 'schema-website', data: websiteSchema },
      { id: 'schema-webpage', data: webPageSchema },
      { id: 'schema-breadcrumb', data: breadcrumbSchema },
      { id: 'schema-faq', data: faqSchema },
    ];

    schemas.forEach(({ id, data }) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      script.id = id;
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach(({ id }) => {
        document.getElementById(id)?.remove();
      });
    };
  }, []);

  /* -------------------------------------------------------------------------
   * Scroll listener effect
   * ----------------------------------------------------------------------- */
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // initialise state based on current scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  /* -------------------------------------------------------------------------
   * Helper: smooth scroll to a section
   * ----------------------------------------------------------------------- */
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 shadow-md' : 'bg-white/90'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex flex-col leading-none">
              <span
                style={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  letterSpacing: '0.18em',
                }}
                className="text-[#255485] font-bold text-xl sm:text-2xl tracking-widest uppercase"
              >
                LIKES
              </span>
              <span
                style={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  letterSpacing: '0.22em',
                }}
                className="text-[#255485] text-[8px] sm:text-[9px] font-medium tracking-[0.22em] uppercase mt-0.5"
              >
                Co, Ltd
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('vision')}
              className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              {t('nav.vision')}
            </button>

            {/* 事業・実績 ドロップダウン */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                {t('nav.services')}
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    servicesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* 垂れ幕ドロップダウン */}
              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-0 transition-all duration-200 ${
                  servicesOpen
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 -translate-y-2 pointer-events-none'
                }`}
              >
                {/* 三角形の吹き出し */}
                <div className="flex justify-center">
                  <div className="w-3 h-3 bg-[#255485] rotate-45 -mb-1.5 z-10 relative" />
                </div>
                <div className="bg-[#255485] rounded-lg shadow-xl overflow-hidden min-w-[220px]">
                  <Link
                    to="/services/consulting"
                    className="flex items-center gap-3 px-5 py-4 text-white hover:bg-[#1d4166] transition-colors duration-150 text-sm font-medium whitespace-nowrap cursor-pointer border-b border-white/20"
                  >
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-team-line text-base" />
                    </span>
                    {t('nav.consulting')}
                  </Link>
                  <Link
                    to="/services/cashless"
                    className="flex items-center gap-3 px-5 py-4 text-white hover:bg-[#1d4166] transition-colors duration-150 text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-bank-card-line text-base" />
                    </span>
                    {t('nav.cashless')}
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/case-study"
              className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              {t('nav.caseStudy')}
            </Link>

            <button
              onClick={() => scrollToSection('csr')}
              className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              {t('nav.csr')}
            </button>
            <button
              onClick={() => scrollToSection('company')}
              className="text-gray-700 hover:text-[#255485] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              {t('nav.company')}
            </button>

            {/* Language Switcher */}
            <LanguageSwitcher />

            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[#255485] text-white px-6 py-2.5 rounded-lg hover:bg-[#1d4166] transition-colors duration-200 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              {t('nav.contact')}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-[#255485] transition-colors cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden bg-white border-t border-gray-200 transition-all duration-300 overflow-hidden ${
            mobileMenuOpen ? 'max-h-screen' : 'max-h-0'
          }`}
        >
          <nav className="px-4 py-4 space-y-2">
            {/* Mobile Language Switcher */}
            <div className="flex items-center px-4 py-2 gap-3">
              <span className="text-xs text-gray-500 font-medium">Language</span>
              <LanguageSwitcher />
            </div>
            <button
              onClick={() => scrollToSection('vision')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] transition-colors rounded-lg text-sm font-medium cursor-pointer"
            >
              {t('nav.vision')}
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] transition-colors rounded-lg text-sm font-medium cursor-pointer"
            >
              {t('nav.services')}
            </button>
            <Link
              to="/services/consulting"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full text-left px-8 py-2 text-gray-600 hover:bg-gray-50 hover:text-[#255485] transition-colors rounded-lg text-sm cursor-pointer"
            >
              {t('nav.consulting')}
            </Link>
            <Link
              to="/services/cashless"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full text-left px-8 py-2 text-gray-600 hover:bg-gray-50 hover:text-[#255485] transition-colors rounded-lg text-sm cursor-pointer"
            >
              {t('nav.cashless')}
            </Link>
            <Link
              to="/case-study"
              onClick={() => setMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] transition-colors rounded-lg text-sm font-medium cursor-pointer"
            >
              {t('nav.caseStudy')}
            </Link>
            <button
              onClick={() => scrollToSection('csr')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] transition-colors rounded-lg text-sm font-medium cursor-pointer"
            >
              {t('nav.csr')}
            </button>
            <button
              onClick={() => scrollToSection('company')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#255485] transition-colors rounded-lg text-sm font-medium cursor-pointer"
            >
              {t('nav.company')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-3 bg-[#255485] text-white hover:bg-[#1d4166] transition-colors rounded-lg text-sm font-medium cursor-pointer"
            >
              {t('nav.contact')}
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Hero />
        <Vision />
        <Services />
        <CSR />
        <CompanyProfile />
        <Contact />
      </main>

      <Footer />
    </div>
  );
}